import { inject, Injectable } from '@angular/core';

import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';

import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { ShopStoreDispatcher } from '@common/dispatchers/shop.dispatcher';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { BasketStoreDispatcher } from '@common/dispatchers/basket.dispatcher';
import { FilterStoreDispatcher } from '@common/dispatchers/filter.dispatcher';
import { AnchorStoreDispatcher } from '@common/dispatchers/anchor.dispatcher';
import { EntityStoreDispatcher } from '@common/dispatchers/entity.dispatcher';
import { ProductStoreDispatcher } from '@common/dispatchers/product.dispatcher';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';
import { DeliveryStoreDispatcher } from '@common/dispatchers/delivery.dispatcher';
import { AuthStoreDispatcher } from '@common/dispatchers/authentication.dispatcher';
import { DeclinableStoreDispatcher } from '@common/dispatchers/declinable.dispatcher';
import { CartFunnelStoreDispatcher } from '@common/dispatchers/cart-funnel.dispatcher';
import { MarketplaceStoreDispatcher } from '@common/dispatchers/marketplace.dispatcher';
import { TransactionStoreDispatcher } from '@common/dispatchers/transaction.dispatcher';
import { OnSiteLocationStoreDispatcher } from '@common/dispatchers/on-site-location.dispatcher';
import { DisplayCurrencyService } from './display-currency/display-currency.service';
import { ChangeLanguageService } from './i18n/change-language.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { ShopCatalogFiltersService } from '@shared/blocks/shop-product-list-block/shop-catalog-filters.service';
import { filter, from, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';

@Injectable({
  providedIn: 'root',
})
export class ResetStateService {
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);

  constructor(
    private authDispatcher: AuthStoreDispatcher,
    private cartDispatcher: CartStoreDispatcher,
    private shopDispatcher: ShopStoreDispatcher,
    private orderDispatcher: OrderStoreDispatcher,
    private entityDispatcher: EntityStoreDispatcher,
    private filterDispatcher: FilterStoreDispatcher,
    private basketDispatcher: BasketStoreDispatcher,
    private anchorDispatcher: AnchorStoreDispatcher,
    private paymentDispatcher: PaymentStoreDispatcher,
    private productDispatcher: ProductStoreDispatcher,
    private deliveryDispatcher: DeliveryStoreDispatcher,
    private cartFunnelDispatcher: CartFunnelStoreDispatcher,
    private declinableDispatcher: DeclinableStoreDispatcher,
    private marketplaceDispatcher: MarketplaceStoreDispatcher,
    private transactionDispatcher: TransactionStoreDispatcher,
    protected readonly authFirebaseService: AuthFirebaseService,
    private onSiteLocationDispatcher: OnSiteLocationStoreDispatcher,
    private readonly changeLanguageService: ChangeLanguageService,
    private readonly analyticsService: AnalyticsService,
    private readonly authStoreSelector: AuthStoreSelector,
    private readonly catalogFilters: ShopCatalogFiltersService,
    private readonly displayCurrencyService: DisplayCurrencyService,
  ) {}

  reset(): void {
    this.catalogFilters.clear();
    this.anchorDispatcher.reset();
    this.cartFunnelDispatcher.reset();
    this.basketDispatcher.reset();
    this.declinableDispatcher.reset();
    this.deliveryDispatcher.reset();
    this.entityDispatcher.reset();
    this.filterDispatcher.reset();
    this.onSiteLocationDispatcher.reset();
    this.orderDispatcher.reset();
    this.paymentDispatcher.reset();
    this.productDispatcher.reset();
    this.shopDispatcher.reset();
    this.transactionDispatcher.reset();
    this.cartDispatcher.reset();
    this.marketplaceDispatcher.reset();
    this.authDispatcher.reset();
    this.displayCurrencyService.resetSelection();
    // todo reset order error
  }

  // TODO: Change this to observable
  refreshUI({
    redirectToHomePage = true,
  }: { redirectToHomePage?: boolean } = {}): Observable<true> {
    // The order-status timer calls this, then `goToHome()` does
    // `window.location.href = '/'`. That reload drops `cbPreview` and the next
    // boot reads another tab's `marketplace_domain`. Stay on the preview stub.
    if (this.cb2PreviewState.isActive()) {
      return of(true);
    }

    this.reset();

    return from(this.authFirebaseService.disconnectUser()).pipe(
      switchMap(() => {
        // wait for the user to be fully removed
        return this.authStoreSelector.selectUser$.pipe(filter(user => !user));
      }),
      take(1),
      tap(() => (redirectToHomePage ? this.goToHome() : null)),
      map(() => true as const),
    );
  }

  // todo observable
  public async refreshUIAndResetLang() {
    if (this.cb2PreviewState.isActive()) {
      return;
    }

    const locale = this.changeLanguageService.resetLanguage();
    this.reset();
    // Reset analytics session ID to consider this as a new session
    this.analyticsService.resetSession();
    await this.authFirebaseService.disconnectUser();
    this.goToHome(locale);
  }

  goToHome(lang?: string): void {
    if (this.cb2PreviewState.isActive()) {
      return;
    }

    // todo find a way to stop the execution of any code and wait for window.location.href to complete. It may avoid future errors
    window.location.href = `/${lang ?? ''}`; // Redirect to the home page, with the current language if provided
  }
}
